import React, { useMemo, useState } from 'react';

import { gql } from '@apollo/client';
import { formatDateRangeIntl, useFormatDateTime, useTranslation } from '@modernloop/i18n';
import { assertIsoDate, getLocalTimezone, getTZAbbr } from '@modernloop/shared/datetime';
import { getTimeRangesInTimezone } from '@modernloop/shared/utils';
import { Button, Divider, Paper, Stack, Typography } from '@mui/material';

import { CandidateAvailabilitySidebarSection_TaskFragment } from 'src/generated/mloop-graphql';

import { FCWithFragments } from 'src/components/types';

import { assertIsoTimestamp } from 'src/types/IsoTimestamp';

import { formatToTimeZone } from 'src/utils/datetime/Format';
import { getTimezoneDisplayName } from 'src/utils/datetime/Timezone';

const DEFAULT_VISIBLE_DAYS = 3;

type Props = {
  task: CandidateAvailabilitySidebarSection_TaskFragment;
};

type CandidateAvailabilityRange = NonNullable<
  CandidateAvailabilitySidebarSection_TaskFragment['availabilities'][number]['candidateEnteredAvailability']
>[number];

type CandidateAvailabilityByDate = {
  date: string;
  ranges: CandidateAvailabilityRange[];
};

const CandidateAvailabilitySidebarSection: FCWithFragments<
  { task: CandidateAvailabilitySidebarSection_TaskFragment },
  Props
> = ({ task }) => {
  const { t } = useTranslation();
  const formatDate = useFormatDateTime();
  const [showAllDays, setShowAllDays] = useState(false);

  const candidateTimezone = task.application.candidate?.timezone;
  const timezone = candidateTimezone || getLocalTimezone();
  const candidateAvailabilities =
    task.availabilities?.flatMap(({ candidateEnteredAvailability }) => candidateEnteredAvailability ?? []) ?? [];

  const candidateAvailabilityByDate = useMemo((): CandidateAvailabilityByDate[] => {
    if (!candidateAvailabilities.length) {
      return [];
    }

    const groupedAvailabilities: Record<string, CandidateAvailabilityRange[]> = {};
    const sortedAvailabilities = [...getTimeRangesInTimezone(candidateAvailabilities, timezone)].sort(
      (availabilityA, availabilityB) => availabilityA.startAt.localeCompare(availabilityB.startAt)
    );

    sortedAvailabilities.forEach((availability) => {
      const dateKey = formatToTimeZone(assertIsoTimestamp(availability.startAt), 'yyyy-MM-dd', timezone);

      if (!groupedAvailabilities[dateKey]) {
        groupedAvailabilities[dateKey] = [];
      }

      groupedAvailabilities[dateKey].push(availability);
    });

    return Object.entries(groupedAvailabilities)
      .sort(([dateA], [dateB]) => dateA.localeCompare(dateB))
      .map(([date, ranges]) => ({ date, ranges }));
  }, [candidateAvailabilities, timezone]);

  const hasAvailability = candidateAvailabilityByDate.length > 0;
  const shouldShowExpandButton = candidateAvailabilityByDate.length > DEFAULT_VISIBLE_DAYS;
  const visibleAvailabilityByDate = showAllDays
    ? candidateAvailabilityByDate
    : candidateAvailabilityByDate.slice(0, DEFAULT_VISIBLE_DAYS);

  return (
    <Stack spacing={1.5} data-testid="schedule-task-candidate-availability-sidebar-section">
      <Divider />
      <Typography fontWeight={600}>{t('hiring-dashboard-tasks:tasks.availability.candidateAvailability')}</Typography>

      {!hasAvailability ? (
        <Stack spacing={1}>
          <Typography color="text.secondary" variant="body2">
            {t('hiring-dashboard-candidates:candidates.availability.noAvailability')}
          </Typography>
        </Stack>
      ) : (
        <>
          <Paper variant="outlined" sx={{ p: 1.5 }}>
            <Stack spacing={1}>
              {visibleAvailabilityByDate.map((availabilityByDate) => {
                const { date, ranges } = availabilityByDate;

                return (
                  <Stack
                    key={date}
                    direction="row"
                    spacing={1.5}
                    alignItems="flex-start"
                    data-testid="schedule-task-candidate-availability-day-group"
                  >
                    <Typography variant="body2" sx={{ minWidth: '128px' }}>
                      {formatDate(assertIsoDate(date), 'MonthDayYearLong')}
                    </Typography>

                    <Stack spacing={0.5} sx={{ minWidth: 0 }}>
                      {ranges.map((availability) => {
                        // Same formatting as the Schedules tab availability view so both
                        // surfaces stay in visual parity; Intl.formatRange collapses the
                        // shared meridiem and shows both when they differ.
                        const timeRangeLabel = formatDateRangeIntl(
                          assertIsoTimestamp(availability.startAt),
                          assertIsoTimestamp(availability.endAt),
                          'TimeShort',
                          { timeZone: timezone }
                        );

                        return (
                          <Typography
                            key={`${availability.startAt}-${availability.endAt}`}
                            color="text.secondary"
                            variant="body2"
                            data-testid="schedule-task-candidate-availability-time-range"
                          >
                            {`${timeRangeLabel} ${getTZAbbr(timezone)}`}
                          </Typography>
                        );
                      })}
                    </Stack>
                  </Stack>
                );
              })}
            </Stack>
          </Paper>

          {shouldShowExpandButton && (
            <Button
              size="small"
              onClick={() => setShowAllDays((previousValue) => !previousValue)}
              sx={{ alignSelf: 'flex-start' }}
              data-testid="schedule-task-candidate-availability-expand-button"
            >
              {showAllDays
                ? t('hiring-dashboard-candidates:candidates.activityLog.showLess')
                : t('hiring-dashboard-schedule:schedule.viewAllDays')}
            </Button>
          )}

          {candidateTimezone && (
            <Typography color="text.secondary" variant="body2">
              {t('hiring-dashboard-candidates:candidates.availability.shownInCandidateTimezone', {
                timezone: getTimezoneDisplayName(timezone),
              })}
            </Typography>
          )}
        </>
      )}
    </Stack>
  );
};

CandidateAvailabilitySidebarSection.fragments = {
  task: gql`
    fragment CandidateAvailabilitySidebarSection_task on Task {
      availabilities {
        candidateEnteredAvailability {
          startAt
          endAt
        }
      }
      application {
        candidate {
          timezone
        }
      }
    }
  `,
};

export default CandidateAvailabilitySidebarSection;
